import { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Calendar, User, FileText, History } from 'lucide-react';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import auditHistoryService from '../../../../../../services/audit_services/auditHistoryService';

export default function AuditPlanHistoryDetails() {
  const { historyId } = useParams();
  const [searchParams] = useSearchParams();
  const { authTokens, hasRole } = useContext(AuthContext);
  const { showError } = useToast();
  const navigate = useNavigate();

  const [historyRecord, setHistoryRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  // Determine if user is AuditManager or AuditUser
  const isAuditManager = hasRole('AuditManager');
  
  // Get view mode from URL params (for AuditManager toggle)
  const viewMode = searchParams.get('view') || 'all';

  useEffect(() => {
    fetchHistoryDetails();
  }, [historyId]);

  const fetchHistoryDetails = async () => {
    setLoading(true);
    
    try {
      // Use appropriate service method based on role and view mode
      // AuditUser always sees "my" view, AuditManager can toggle
      const shouldUseMyView = !isAuditManager || viewMode === 'my';
      
      const result = shouldUseMyView
        ? await auditHistoryService.getMyAuditPlanHistoryDetail(authTokens.access, historyId)
        : await auditHistoryService.getAuditPlanHistoryDetail(authTokens.access, historyId);
      
      if (result.success) {
        setHistoryRecord(result.data);
      } else {
        showError(result.error || "Erreur lors du chargement des détails de l'historique");
        navigate('/dashboard/audit/history/plans');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching history details:', error);
      showError("Erreur lors du chargement des détails de l'historique");
      setLoading(false);
    }
  };

  const getActionBadge = (actionType) => {
    const actionConfig = {
      create: { bg: 'bg-green-100', text: 'text-green-800', label: 'Créé' },
      update: { bg: 'bg-green-100', text: 'text-green-800', label: 'Mis à jour' },
      delete: { bg: 'bg-red-100', text: 'text-red-800', label: 'Supprimé' },
      release: { bg: 'bg-green-100', text: 'text-green-800', label: 'Diffusé' },
      start: { bg: 'bg-green-100', text: 'text-green-800', label: 'Démarré' },
      complete: { bg: 'bg-green-100', text: 'text-green-800', label: 'Terminé' },
      close: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Clôturé' },
      achieve: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Réalisé' },
      unachieve: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Non réalisé' },
      reopen: { bg: 'bg-green-100', text: 'text-green-800', label: 'Rouvert' },
      review: { bg: 'bg-green-100', text: 'text-green-800', label: 'Revu' },
      publish: { bg: 'bg-green-100', text: 'text-green-800', label: 'Publié' },
      pause_publication: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Publication en pause' }
    };

    const config = actionConfig[actionType] || { bg: 'bg-gray-100', text: 'text-gray-800', label: actionType };
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderChangesSummary = (changes) => {
    if (!changes || Object.keys(changes).length === 0) {
      return <p className="text-gray-500 italic">Aucun détail de modification enregistré</p>;
    }

    const renderValue = (value) => {
      if (value === null || value === undefined) {
        return '(vide)';
      }
      if (Array.isArray(value)) {
        return (
          <ul className="list-disc list-inside ml-2">
            {value.map((item, idx) => (
              <li key={idx}>{String(item)}</li>
            ))}
          </ul>
        );
      }
      if (typeof value === 'object') {
        return <pre className="text-xs bg-gray-50 p-2 rounded mt-1">{JSON.stringify(value, null, 2)}</pre>;
      }
      return String(value);
    };

    return (
      <div className="space-y-3">
        {Object.entries(changes).map(([field, change]) => {
          // Handle both "old/new" and "from/to" formats
          const oldValue = change.old !== undefined ? change.old : change.from;
          const newValue = change.new !== undefined ? change.new : change.to;

          return (
            <div key={field} className="border-l-4 border-green-500 pl-4 py-2">
              <p className="text-sm font-medium text-gray-700 capitalize">
                {field.replace(/_/g, ' ')}
              </p>
              <div className="mt-1 space-y-2">
                {oldValue !== undefined && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Avant :</span>{' '}
                    <span className="text-red-600">
                      {renderValue(oldValue)}
                    </span>
                  </div>
                )}
                {newValue !== undefined && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Après :</span>{' '}
                    <span className="text-green-600">
                      {renderValue(newValue)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des détails de l'historique...</p>
        </div>
      </div>
    );
  }

  if (!historyRecord) {
    return (
      <div className="text-center py-12">
        <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Enregistrement d'historique introuvable</p>
        <Link
          to="/dashboard/audit/history/plans"
          className="mt-4 inline-flex items-center text-green-600 hover:text-green-800"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour à l'historique
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/dashboard/audit/history/plans')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Détails de l'enregistrement d'historique</h1>
            <p className="text-sm text-gray-600 mt-1">
              {viewMode === 'my' ? "Mon historique de plans d'audit" : "Historique des plans d'audit"}
            </p>
          </div>
        </div>
        {getActionBadge(historyRecord.action_type)}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations sur l'action</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Titre du plan</label>
                <p className="mt-1 text-gray-900 font-medium">{historyRecord.audit_plan_title}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Description</label>
                <p className="mt-1 text-gray-900">{historyRecord.description}</p>
              </div>

              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span>{formatDate(historyRecord.action_date)}</span>
              </div>

              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <User className="h-4 w-4 text-gray-400" />
                <span>Effectué par : {historyRecord.done_by_name || 'Inconnu'}</span>
              </div>
            </div>
          </div>

          {/* Changes Summary */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-green-600" />
              Résumé des modifications
            </h2>
            {renderChangesSummary(historyRecord.changes_summary)}
          </div>
        </div>

        {/* Right Column - Metadata */}
        <div className="space-y-6">
          {/* Record Metadata */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Métadonnées</h2>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">ID de l'enregistrement</label>
                <p className="mt-1 text-sm text-gray-900">#{historyRecord.id}</p>
              </div>

              {historyRecord.audit_plan && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">ID du plan</label>
                  <p className="mt-1 text-sm text-gray-900">#{historyRecord.audit_plan}</p>
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Type d'action</label>
                <p className="mt-1 text-sm text-gray-900 capitalize">{historyRecord.action_type.replace(/_/g, ' ')}</p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Horodatage</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(historyRecord.action_date)}</p>
              </div>
            </div>
          </div>



        </div>
      </div>
    </div>
  );
}
